import jsPDF from "jspdf";
import Chart from "chart.js/auto";

interface FinancialItem {
  category: string;
  value: string | number;
}

interface ChartData {
  labels: string[];
  data: number[];
}

interface MultiDataset {
  labels: string[];
  datasets: Array<{ label: string; data: number[] }>;
}

export interface EnergyCosts {
  energy: number[];
  energyTime: number[];
  incrementalEnergy: number[];
  demand: number[];
  demandTime: number[];
  incrementalDemand: number[];
  serviceCharge: number[];
  otherCharges: number[];
  total: number[];
}

export interface FleetReportDetails {
  reportName: string;
  netSavings: number;
  annualSavings: number;
  address: string;
  city?: string;
  zipCode?: string;
  fleetSize: number;
  analysisYears: number;
  paybackPeriod: number;
  financialTable?: FinancialItem[];
  taxBenefitsTable?: FinancialItem[];
  environmentalTable?: FinancialItem[];
  costOverTimeData?: MultiDataset;
  cumulativeSavingsData?: ChartData;
  environmentalData?: ChartData;
  utilityDetails?: {
    state: string;
    zipCode: string;
    utilityName: string;
    utilityId: string;
    scheduleId: string;
    totalCost: number;
  };
  energyCosts?: EnergyCosts;
  utilityRate?: number;
  applyTaxBenefits?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface WhiteLabelConfig {
  brandLogo: string;
  brandColor: string;
  textColor: string;
  bgColor: string;
  domainURL: string;
  termsOfServiceURL: string;
  privacyPolicyURL: string;
}

const CompanyLogo = require("../images/png/RightSite Rectangle Black copy.png");

const PDF_IMAGE_QUALITY = 0.72;
const CHART_EXPORT_WIDTH = 600;
const CHART_EXPORT_HEIGHT = 360;
const MAP_EXPORT_WIDTH = 640;
const MAP_EXPORT_HEIGHT = 320;

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = (error) => reject(error);
    image.src = src;
  });

const compressImageDataUrl = async (
  dataUrl: string,
  {
    maxWidth,
    maxHeight,
    quality = PDF_IMAGE_QUALITY,
    format = "image/jpeg",
    backgroundColor = "#ffffff",
  }: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    format?: "image/jpeg" | "image/png";
    backgroundColor?: string;
  } = {}
): Promise<string> => {
  if (!dataUrl || !dataUrl.startsWith("data:image")) {
    return dataUrl;
  }

  try {
    const image = await loadImageElement(dataUrl);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return dataUrl;
    }

    const widthScale = maxWidth ? maxWidth / image.width : 1;
    const heightScale = maxHeight ? maxHeight / image.height : 1;
    const scale = Math.min(1, widthScale, heightScale);

    const targetWidth = Math.max(1, Math.floor(image.width * scale));
    const targetHeight = Math.max(1, Math.floor(image.height * scale));

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    if (format === "image/jpeg") {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, targetWidth, targetHeight);
    }

    ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

    return canvas.toDataURL(format, quality);
  } catch (error) {
    console.error("Image compression failed:", error);
    return dataUrl;
  }
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return { r, g, b };
};

const createStaticMapImage = async (
  address: string,
  retries = 2
): Promise<string | null> => {
  const isValidAddress =
    address && address.trim() && address.toLowerCase() !== "n/a";
  const validAddress = isValidAddress
    ? address.trim()
    : "981 US Route 1, Edison, NJ, USA";
  const encodedAddress = encodeURIComponent(validAddress);

  let center = encodedAddress;
  let marker = encodedAddress;
  let zoom = 10;

  if (window.google && window.google.maps) {
    try {
      const geocoder = new window.google.maps.Geocoder();
      const { results } = await new Promise<{
        results: google.maps.GeocoderResult[];
      }>((resolve, reject) => {
        geocoder.geocode({ address: validAddress }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results) {
            resolve({ results });
          } else {
            reject(new Error(`Geocode status: ${status}`));
          }
        });
      });

      if (results[0]) {
        const pos = results[0].geometry.location;
        center = `${pos.lat()},${pos.lng()}`;
        marker = `${pos.lat()},${pos.lng()}`;
        zoom = 15;
      }
    } catch (error) {
      console.error("Geocoding failed:", error);
    }
  }

  const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=${zoom}&size=${MAP_EXPORT_WIDTH}x${MAP_EXPORT_HEIGHT}&scale=1&maptype=roadmap&markers=color:red%7C${marker}&key=AIzaSyDZsmG9zzmlEZLY3BpPYeJMfUphIobw-sM`;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(
        `Fetching static map for address: ${validAddress} (Attempt ${attempt}/${retries})`
      );
      const response = await fetch(staticMapUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      if (!blob.type.startsWith("image/")) {
        throw new Error("Received non-image response from Google Maps API");
      }

      const imageData = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          if (result.startsWith("data:image")) {
            resolve(result);
          } else {
            reject(new Error("Invalid image data URL"));
          }
        };
        reader.onerror = () =>
          reject(new Error("Failed to read map image blob"));
        reader.readAsDataURL(blob);
      });

      if (!isValidAddress) {
        console.warn(
          `Using fallback address: ${validAddress} due to invalid input address: ${address}`
        );
      }

      return compressImageDataUrl(imageData, {
        maxWidth: MAP_EXPORT_WIDTH,
        maxHeight: MAP_EXPORT_HEIGHT,
        quality: PDF_IMAGE_QUALITY,
        format: "image/jpeg",
      });
    } catch (error) {
      console.error(
        `Failed to create static map for address: ${validAddress} (Attempt ${attempt}/${retries}):`,
        error
      );
      if (attempt === retries) {
        console.error("All retries failed for static map image");
        return null;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }
  return null;
};

const generateFleetCharts = async (details: FleetReportDetails) => {
  const charts: { [key: string]: Chart | undefined } = {};

  // Chart colors
  const ICE_RED = "#b91c1c";
  const EV_GREEN = "#16a34a";
  const BRAND_COLOR = "#182444";

  // Chart background plugin
  const chartBackgroundPlugin = {
    id: "chartBackground",
    beforeDraw: (chart: any) => {
      const ctx = chart.ctx as CanvasRenderingContext2D;
      ctx.save();
      ctx.fillStyle = "#fafcfb";
      ctx.fillRect(0, 0, chart.width, chart.height);
      ctx.restore();
    },
  };

  // Break-even plugin for cumulative chart
  const breakEvenPlugin = {
    id: "breakeven",
    afterDraw(chart: any) {
      const {
        ctx,
        chartArea: { left, right },
        scales: { y },
      } = chart;
      ctx.save();
      ctx.strokeStyle = "rgba(0,0,0,0.35)";
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      try {
        const y0 = y.getPixelForValue(0);
        ctx.moveTo(left, y0);
        ctx.lineTo(right, y0);
        ctx.stroke();
      } catch (error) {
        console.warn("Error drawing break-even line:", error);
      } finally {
        ctx.restore();
      }
    },
  };

  // Cost Over Time Chart
  if (details.costOverTimeData) {
    const costOverTimeCanvas = document.createElement("canvas");
    costOverTimeCanvas.width = CHART_EXPORT_WIDTH;
    costOverTimeCanvas.height = CHART_EXPORT_HEIGHT;
    const costOverTimeCtx = costOverTimeCanvas.getContext("2d");
    if (costOverTimeCtx) {
      charts.costOverTime = new Chart(costOverTimeCtx, {
        type: "line",
        data: {
          labels: details.costOverTimeData.labels,
          datasets: details.costOverTimeData.datasets.map((dataset, index) => ({
            ...dataset,
            borderColor: index === 0 ? ICE_RED : EV_GREEN,
            backgroundColor: `rgba(${
              index === 0 ? "185, 28, 28" : "22, 163, 74"
            }, 0.1)`,
            fill: false,
            tension: 0.4,
            borderWidth: 2,
            pointBackgroundColor: index === 0 ? ICE_RED : EV_GREEN,
          })),
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          animation: false,
          scales: {
            y: {
              beginAtZero: false,
              title: {
                display: true,
                text: "Annual Cost ($)",
                font: { size: 14 },
              },
              grid: { color: "#e0e0e0" },
              ticks: {
                font: { size: 12 },
                callback: (value) => `$${Number(value).toLocaleString()}`,
              },
            },
            x: {
              title: { display: true, text: "Year", font: { size: 14 } },
              grid: { color: "#e0e0e0" },
              ticks: { font: { size: 12 } },
            },
          },
          plugins: {
            legend: {
              position: "top",
              labels: { font: { size: 14 }, padding: 20 },
            },
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  const label = ctx.dataset.label || "";
                  const value = ctx.parsed.y;
                  return `${label}: $${Number(value).toLocaleString()}`;
                },
              },
            },
          },
        },
        plugins: [chartBackgroundPlugin],
      });
    }
  }

  // Cumulative Savings Chart
  if (details.cumulativeSavingsData) {
    const cumulativeCanvas = document.createElement("canvas");
    cumulativeCanvas.width = CHART_EXPORT_WIDTH;
    cumulativeCanvas.height = CHART_EXPORT_HEIGHT;
    const cumulativeCtx = cumulativeCanvas.getContext("2d");
    if (cumulativeCtx) {
      charts.cumulativeSavings = new Chart(cumulativeCtx, {
        type: "line",
        data: {
          labels: details.cumulativeSavingsData.labels,
          datasets: [
            {
              label: "Cumulative Net Savings",
              data: details.cumulativeSavingsData.data,
              borderColor: BRAND_COLOR,
              backgroundColor: "rgba(24, 36, 68, 0.1)",
              fill: true,
              tension: 0.4,
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          animation: false,
          scales: {
            y: {
              title: {
                display: true,
                text: "Cumulative Savings ($)",
                font: { size: 14 },
              },
              grid: { color: "#e0e0e0" },
              ticks: {
                font: { size: 12 },
                callback: (value) => `$${Number(value).toLocaleString()}`,
              },
            },
            x: {
              title: { display: true, text: "Year", font: { size: 14 } },
              grid: { color: "#e0e0e0" },
              ticks: { font: { size: 12 } },
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: { font: { size: 14 }, padding: 20 },
            },
          },
        },
        plugins: [breakEvenPlugin, chartBackgroundPlugin],
      });
    }
  }

  // Environmental Impact Chart
  if (details.environmentalData) {
    const environmentalCanvas = document.createElement("canvas");
    environmentalCanvas.width = CHART_EXPORT_WIDTH;
    environmentalCanvas.height = CHART_EXPORT_HEIGHT;
    const environmentalCtx = environmentalCanvas.getContext("2d");
    if (environmentalCtx) {
      charts.environmental = new Chart(environmentalCtx, {
        type: "bar",
        data: {
          labels: details.environmentalData.labels,
          datasets: [
            {
              label: "Environmental Impact",
              data: details.environmentalData.data,
              backgroundColor: [BRAND_COLOR, "#4b5b5d", EV_GREEN],
              borderColor: [BRAND_COLOR, "#4b5b5d", EV_GREEN],
              borderWidth: 1,
              borderSkipped: false,
              barPercentage: 0.5,
              categoryPercentage: 0.5,
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          animation: false,
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: "Amount", font: { size: 14 } },
              grid: { color: "#e0e0e0" },
              ticks: { font: { size: 12 } },
            },
            x: {
              ticks: {
                font: { size: 10 },
                maxRotation: 45,
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  return `${ctx.label}: ${Number(
                    ctx.parsed.y
                  ).toLocaleString()}`;
                },
              },
            },
          },
        },
        plugins: [chartBackgroundPlugin],
      });
    }
  }

  // Cost Comparison Chart (Annual)
  const costComparisonCanvas = document.createElement("canvas");
  costComparisonCanvas.width = CHART_EXPORT_WIDTH;
  costComparisonCanvas.height = CHART_EXPORT_HEIGHT;
  const costComparisonCtx = costComparisonCanvas.getContext("2d");
  if (costComparisonCtx) {
    const iceOpex = details.financialTable?.find(
      (item) =>
        item.category === "Annual ICE Opex" ||
        item.category.includes("ICE Opex")
    )?.value;
    const evOpex = details.financialTable?.find(
      (item) =>
        item.category === "Annual EV Opex" || item.category.includes("EV Opex")
    )?.value;

    const iceValue =
      typeof iceOpex === "string"
        ? parseFloat(iceOpex.replace(/[^0-9.-]+/g, ""))
        : typeof iceOpex === "number"
        ? iceOpex
        : 0;

    const evValue =
      typeof evOpex === "string"
        ? parseFloat(evOpex.replace(/[^0-9.-]+/g, ""))
        : typeof evOpex === "number"
        ? evOpex
        : 0;

    charts.costComparison = new Chart(costComparisonCtx, {
      type: "bar",
      data: {
        labels: ["ICE Opex", "EV Opex"],
        datasets: [
          {
            label: "Annual Cost (USD)",
            data: [iceValue, evValue],
            backgroundColor: [ICE_RED, EV_GREEN],
            borderColor: [ICE_RED, EV_GREEN],
            borderWidth: 1,
            borderSkipped: false,
            barPercentage: 0.5,
            categoryPercentage: 0.5,
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: "Annual Cost ($)",
              font: { size: 14 },
            },
            grid: { color: "#e0e0e0" },
            ticks: {
              font: { size: 12 },
              callback: (value) => `$${Number(value).toLocaleString()}`,
            },
          },
          x: {
            ticks: { font: { size: 12 } },
          },
        },
      },
      plugins: [chartBackgroundPlugin],
    });
  }

  // Wait for charts to render
  await new Promise((resolve) => setTimeout(resolve, 1000));

  return charts;
};

const splitTextIntoLines = (
  text: string,
  maxWidth: number,
  pdf: jsPDF,
  fontSize: number = 8
): string[] => {
  const lines: string[] = [];
  const words = text.split(" ");
  let currentLine = "";

  const currentFontSize = pdf.getFontSize();
  pdf.setFontSize(fontSize);

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = pdf.getTextWidth(testLine);

    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  pdf.setFontSize(currentFontSize);
  return lines;
};

// Add this helper function before generateFleetCharts
const validateAndFixChartData = (
  details: FleetReportDetails
): FleetReportDetails => {
  // Create a deep clone of the details object to avoid modifying read-only properties
  const fixedDetails = JSON.parse(JSON.stringify(details));

  // Fix costOverTimeData if it starts with 0
  if (fixedDetails.costOverTimeData) {
    const iceDataset = fixedDetails.costOverTimeData.datasets.find((d: any) =>
      d.label.includes("ICE")
    );
    const evDataset = fixedDetails.costOverTimeData.datasets.find((d: any) =>
      d.label.includes("EV")
    );

    // Get current year values from financial table
    const iceOpexCurrent = fixedDetails.financialTable?.find(
      (item: FinancialItem) =>
        item.category === "Annual ICE Opex" ||
        item.category.includes("ICE Opex")
    )?.value;

    const evOpexCurrent = fixedDetails.financialTable?.find(
      (item: FinancialItem) =>
        item.category === "Annual EV Opex" || item.category.includes("EV Opex")
    )?.value;

    const iceValue =
      typeof iceOpexCurrent === "string"
        ? parseFloat(iceOpexCurrent.replace(/[^0-9.-]+/g, ""))
        : typeof iceOpexCurrent === "number"
        ? iceOpexCurrent
        : 0;

    const evValue =
      typeof evOpexCurrent === "string"
        ? parseFloat(evOpexCurrent.replace(/[^0-9.-]+/g, ""))
        : typeof evOpexCurrent === "number"
        ? evOpexCurrent
        : 0;

    // If first data point is 0, replace it with current year values
    if (iceDataset && iceDataset.data.length > 0 && iceDataset.data[0] === 0) {
      iceDataset.data[0] = iceValue;
    }

    if (evDataset && evDataset.data.length > 0 && evDataset.data[0] === 0) {
      evDataset.data[0] = evValue;
    }
  }

  // Ensure energy costs arrays are properly formatted
  if (fixedDetails.energyCosts) {
    const energyCostFields = [
      "energy",
      "energyTime",
      "incrementalEnergy",
      "demand",
      "demandTime",
      "incrementalDemand",
      "serviceCharge",
      "otherCharges",
      "total",
    ];

    energyCostFields.forEach((field) => {
      if (
        fixedDetails.energyCosts &&
        fixedDetails.energyCosts[field as keyof typeof fixedDetails.energyCosts]
      ) {
        fixedDetails.energyCosts[
          field as keyof typeof fixedDetails.energyCosts
        ] = toNumArr(
          fixedDetails.energyCosts[
            field as keyof typeof fixedDetails.energyCosts
          ]
        );
      }
    });
  }

  return fixedDetails;
};

// Add this helper function if not present
const toNumArr = (arr: any, len = 5) =>
  Array.isArray(arr) ? arr.map((x) => Number(x) || 0) : Array(len).fill(0);

// Add this helper function near the other helper functions
const hasRealUtilityData = (details: FleetReportDetails): boolean => {
  const u = details.utilityDetails;
  const e = details.energyCosts;

  const hasValidUtilityInfo =
    u &&
    ((u.state && u.state.trim() !== "--") ||
      (u.zipCode && u.zipCode.trim() !== "--") ||
      (u.utilityName && u.utilityName.trim() !== "--") ||
      u.totalCost > 0);

  const hasNonZeroCosts =
    e &&
    [
      ...toNumArr(e.total),
      ...toNumArr(e.energy),
      ...toNumArr(e.demand),
      ...toNumArr(e.incrementalEnergy),
      ...toNumArr(e.incrementalDemand),
    ].some((v) => v > 0.01);

  return (hasValidUtilityInfo || hasNonZeroCosts) ?? false;
};

export const downloadFleetReport = async (
  details: FleetReportDetails,
  isWhiteLabel: boolean,
  whiteLabelConfig?: WhiteLabelConfig,
  returnBlob: boolean = false
): Promise<Blob | void> => {
  console.log("Fleet Report White Label Config:", whiteLabelConfig);
  try {
    const fixedDetails = validateAndFixChartData(details);

    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: "a4",
      compress: true,
    });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pdfWidth - margin * 2;
    const chartAspectRatio = CHART_EXPORT_WIDTH / CHART_EXPORT_HEIGHT;
    const chartHeight = contentWidth / chartAspectRatio;
    const mapAspectRatio = MAP_EXPORT_WIDTH / MAP_EXPORT_HEIGHT;
    const mapHeight = contentWidth / mapAspectRatio;

    let currentY = margin;

    const addNewPage = () => {
      pdf.addPage();
      currentY = margin;
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.bgColor);
        pdf.setFillColor(r, g, b);
        pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
      }
    };

    const checkPageBreak = (requiredHeight: number) => {
      if (currentY + requiredHeight > pdfHeight - margin) {
        addNewPage();
      }
    };

    // Set background color for white label
    if (isWhiteLabel && whiteLabelConfig) {
      const { r, g, b } = hexToRgb(whiteLabelConfig.bgColor);
      pdf.setFillColor(r, g, b);
      pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
    }

    const charts = await generateFleetCharts(fixedDetails);
    const mapImageData = await createStaticMapImage(fixedDetails.address);

    // Add user info for embedded reports
    if (fixedDetails.firstName) {
      checkPageBreak(15);
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "italic");
      pdf.text(
        `Customer Name: ${fixedDetails.firstName} ${fixedDetails.lastName}`,
        margin,
        currentY
      );
      currentY += 5;
      pdf.setFontSize(10);
      pdf.text(`Email: ${fixedDetails.email}`, margin, currentY);
      currentY += 15;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(14);
    }

    // Add report title
    checkPageBreak(35);
    pdf.setFontSize(22);
    if (isWhiteLabel && whiteLabelConfig) {
      const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
      pdf.setTextColor(r, g, b);
    } else {
      pdf.setTextColor(75, 91, 93);
    }
    pdf.text(
      isWhiteLabel ? "Fleet EV Report" : "Fleet EV Estimator",
      margin,
      currentY
    );
    currentY += 15;

    // Add logo
    try {
      let logoDataUrl: string;
      const logoSrc =
        isWhiteLabel && whiteLabelConfig?.brandLogo
          ? whiteLabelConfig.brandLogo
          : CompanyLogo;

      logoDataUrl = logoSrc;

      let imageFormat: "PNG" | "JPEG" = "PNG";
      if (typeof logoDataUrl === "string") {
        const lowerLogoSrc = logoDataUrl.toLowerCase();
        if (
          lowerLogoSrc.startsWith("data:image/jpeg") ||
          lowerLogoSrc.startsWith("data:image/jpg") ||
          lowerLogoSrc.endsWith(".jpg") ||
          lowerLogoSrc.endsWith(".jpeg")
        ) {
          imageFormat = "JPEG";
        }
      }

      if (
        typeof logoDataUrl === "string" &&
        logoDataUrl.startsWith("data:image")
      ) {
        const isPng = logoDataUrl.startsWith("data:image/png");
        logoDataUrl = await compressImageDataUrl(logoDataUrl, {
          maxWidth: 240,
          maxHeight: 120,
          quality: isPng ? undefined : 0.82,
          format: isPng ? "image/png" : "image/jpeg",
          backgroundColor: "#ffffff",
        });
        imageFormat = isPng ? "PNG" : "JPEG";
      }

      pdf.addImage(
        logoDataUrl,
        imageFormat,
        pdfWidth - margin - 50,
        currentY - 20,
        40,
        whiteLabelConfig ? 30 : 10,
        undefined,
        "FAST"
      );
      currentY += 10;
    } catch (error) {
      console.error("Failed to add logo:", error);
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text("[Company Logo]", pdfWidth - margin - 50, currentY);
      currentY += 10;
    }

    // Report Name
    pdf.setFontSize(14);
    if (isWhiteLabel && whiteLabelConfig) {
      const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
      pdf.setTextColor(r, g, b);
    } else {
      pdf.setTextColor(75, 91, 93);
    }
    pdf.text(
      fixedDetails.reportName || "Fleet EV Conversion",
      margin,
      currentY
    );
    currentY += 10;

    // Hero Section - Annual Savings
    checkPageBreak(50);
    pdf.setFillColor(isWhiteLabel ? 240 : 244, 240, isWhiteLabel ? 240 : 230);
    pdf.rect(margin, currentY, contentWidth, 40, "F");
    if (isWhiteLabel && whiteLabelConfig) {
      const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
      pdf.setTextColor(r, g, b);
    } else {
      pdf.setTextColor(75, 91, 93);
    }
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text(
      `Estimated Annual Savings: $${fixedDetails.annualSavings.toLocaleString()}`,
      margin + 5,
      currentY + 10
    );
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text("Fuel + Maintenance Cost Savings", margin + 5, currentY + 15);

    // Mini cards data
    const paybackYears = fixedDetails.paybackPeriod;
    const paybackDisplay = !isFinite(paybackYears)
      ? "No payback"
      : `${Math.floor(paybackYears)} yr ${Math.round(
          (paybackYears - Math.floor(paybackYears)) * 12
        )} mo`;

    const miniCards = [
      {
        label: `${fixedDetails.analysisYears}-Year Net Savings`,
        value: `$${fixedDetails.netSavings.toLocaleString()}`,
      },
      {
        label: "Upfront Net Outlay",
        value: `${
          fixedDetails.financialTable?.find(
            (item) => item.category === "Upfront Net Outlay"
          )?.value || "0"
        }`,
      },
      { label: "Payback Period", value: paybackDisplay },
    ];

    pdf.setFontSize(9);
    let miniCardY = currentY + 25;
    miniCards.forEach((card, index) => {
      const xPos = margin + 5 + index * (contentWidth / 3);
      pdf.text(card.label, xPos, miniCardY);
      pdf.setFont("helvetica", "bold");
      pdf.text(card.value, xPos, miniCardY + 4);
      pdf.setFont("helvetica", "normal");
    });

    currentY += 55;

    // Financial Overview Section
    if (fixedDetails.financialTable && fixedDetails.financialTable.length > 0) {
      checkPageBreak(30);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.text("Financial Overview", margin, currentY);
      currentY += 10;

      // Create all 8 financial KPIs matching the ViewFleetReport
      const financialKPIs = [
        {
          label: "Annual ICE Fuel Cost",
          value: fixedDetails.financialTable.find(
            (item) => item.category === "Annual ICE Fuel Cost"
          )?.value,
          smallText: "per fleet",
        },
        {
          label: "Annual EV Energy Cost",
          value: fixedDetails.financialTable.find(
            (item) => item.category === "Annual EV Energy Cost"
          )?.value,
          smallText: "per fleet",
        },
        {
          label: "Annual Fuel + Maint. Savings",
          value: fixedDetails.financialTable.find(
            (item) => item.category === "Annual Savings"
          )?.value,
          smallText: `ICE Opex ${
            fixedDetails.financialTable.find(
              (item) => item.category === "Annual ICE Opex"
            )?.value
          } vs EV Opex ${
            fixedDetails.financialTable.find(
              (item) => item.category === "Annual EV Opex"
            )?.value
          }`,
        },
        {
          label: "Upfront Net Outlay",
          value: fixedDetails.financialTable.find(
            (item) => item.category === "Upfront Net Outlay"
          )?.value,
          smallText: "EVs - incentives - resale + infrastructure",
        },
        {
          label: `${fixedDetails.analysisYears}-Year Net Savings`,
          value: `$${fixedDetails.netSavings.toLocaleString()}`,
          smallText: "Total savings - upfront",
        },
        {
          label: "Payback Period",
          value: !isFinite(fixedDetails.paybackPeriod)
            ? "No payback"
            : `${Math.floor(fixedDetails.paybackPeriod)} yr ${Math.round(
                (fixedDetails.paybackPeriod -
                  Math.floor(fixedDetails.paybackPeriod)) *
                  12
              )} mo`,
          smallText: !isFinite(fixedDetails.paybackPeriod)
            ? "No payback (annual savings ≤ 0)"
            : "Time to recover upfront investment",
        },
        {
          label: "Annual ICE Opex",
          value: fixedDetails.financialTable.find(
            (item) => item.category === "Annual ICE Opex"
          )?.value,
          smallText: "Fuel + maintenance",
        },
        {
          label: "Annual EV Opex",
          value: fixedDetails.financialTable.find(
            (item) => item.category === "Annual EV Opex"
          )?.value,
          smallText: "Energy + maintenance",
        },
      ].filter((kpi) => kpi.value);

      // Create a 2x4 grid layout for the 8 cards (2 cards per row, 4 rows)
      const kpiRows = 4;
      const kpiCols = 2;
      const cardWidth = (contentWidth - 15) / kpiCols;
      const cardHeight = 35;
      const kpiHeight = kpiRows * cardHeight;

      checkPageBreak(kpiHeight + 10);

      // Draw all 8 KPI cards in a grid
      financialKPIs.forEach((kpi, index) => {
        const row = Math.floor(index / kpiCols);
        const col = index % kpiCols;
        const xPos = margin + col * cardWidth + 2;
        const yPos = currentY + row * cardHeight;

        // Card background
        pdf.setFillColor(255, 255, 255);
        pdf.rect(xPos, yPos, cardWidth - 4, cardHeight - 2, "F");
        pdf.setDrawColor(238, 242, 247);
        pdf.rect(xPos, yPos, cardWidth - 4, cardHeight - 2, "S");

        // KPI Label
        pdf.setFontSize(9);
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(107, 114, 128);
        }

        // Handle long labels with text wrapping
        const labelLines = splitTextIntoLines(
          kpi.label,
          cardWidth - 10,
          pdf,
          7
        );
        labelLines.forEach((line, lineIndex) => {
          pdf.text(line, xPos + 5, yPos + 6 + lineIndex * 3);
        });

        // KPI Value
        pdf.setFontSize(11);
        pdf.setFont("helvetica", "bold");
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(24, 36, 68);
        }

        // Position value based on number of label lines
        const valueY = yPos + 6 + labelLines.length * 3 + 5;
        pdf.text(String(kpi.value), xPos + 5, valueY);

        // Small text
        if (kpi.smallText) {
          pdf.setFontSize(7);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(107, 114, 128);

          const smallTextLines = splitTextIntoLines(
            kpi.smallText,
            cardWidth - 10,
            pdf,
            6
          );
          smallTextLines.forEach((line, lineIndex) => {
            pdf.text(line, xPos + 5, valueY + 5 + lineIndex * 2.5);
          });
        }
      });

      currentY += kpiHeight + 15;
    }

    // Environmental Impact Section
    if (
      fixedDetails.environmentalTable &&
      fixedDetails.environmentalTable.length > 0
    ) {
      checkPageBreak(30);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.text("Environmental Impact", margin, currentY);
      currentY += 10;

      const environmentalKPIs = fixedDetails.environmentalTable.slice(0, 4);
      const envRows = Math.ceil(environmentalKPIs.length / 2);
      const envHeight = envRows * 25;

      checkPageBreak(envHeight + 10);

      environmentalKPIs.forEach((kpi, index) => {
        const row = Math.floor(index / 2);
        const col = index % 2;
        const xPos = margin + col * (contentWidth / 2);
        const yPos = currentY + row * 25;

        pdf.setFillColor(245, 245, 245);
        pdf.rect(xPos + 2, yPos, contentWidth / 2 - 4, 20, "F");

        pdf.setFontSize(8);
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }

        // Split long labels
        const labelLines = splitTextIntoLines(
          kpi.category,
          contentWidth / 2 - 10,
          pdf,
          8
        );
        labelLines.forEach((line, lineIndex) => {
          pdf.text(line, xPos + 5, yPos + 7 + lineIndex * 3);
        });

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.text(String(kpi.value), xPos + 5, yPos + 16);
        pdf.setFont("helvetica", "normal");
      });

      currentY += envHeight + 15;
    }

    // Location Details
    checkPageBreak(50);
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    if (isWhiteLabel && whiteLabelConfig) {
      const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
      pdf.setTextColor(r, g, b);
    } else {
      pdf.setTextColor(75, 91, 93);
    }
    pdf.text("Location Details", margin, currentY);
    currentY += 10;

    // Location info
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Address: ${fixedDetails.address}`, margin, currentY);
    currentY += 5;
    if (fixedDetails.city) {
      pdf.text(`City: ${fixedDetails.city}`, margin, currentY);
      currentY += 5;
    }
    if (fixedDetails.zipCode) {
      pdf.text(`Zip Code: ${fixedDetails.zipCode}`, margin, currentY);
      currentY += 20;
    }

    // Location Map
    checkPageBreak(mapHeight + 10);
    if (mapImageData && mapImageData.startsWith("data:image")) {
      pdf.addImage(
        mapImageData,
        "JPEG",
        margin,
        currentY,
        contentWidth,
        mapHeight,
        undefined,
        "FAST"
      );
      currentY += mapHeight + 15;
    } else {
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, currentY, contentWidth, 40, "F");
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(10);
      pdf.text("Map visualization not available", margin + 10, currentY + 20);
      currentY += 100;
    }

    // Utility Information
    if (fixedDetails.utilityDetails) {
      checkPageBreak(100);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.text("Utility Information", margin, currentY);
      currentY += 10;

      // Create utility table with enhanced format
      const utilityData = [
        {
          field: "Utility Name",
          value: fixedDetails.utilityDetails.utilityName || "N/A",
        },
        { field: "State", value: fixedDetails.utilityDetails.state || "N/A" },
        {
          field: "Zip Code",
          value:
            fixedDetails.utilityDetails.zipCode ||
            fixedDetails.zipCode ||
            "N/A",
        },
        {
          field: "Electricity Rate",
          value: `$${(fixedDetails.utilityRate || 0).toFixed(3)}/kWh`,
        },
        {
          field: "Utility ID",
          value: fixedDetails.utilityDetails.utilityId || "N/A",
        },
        {
          field: "Schedule ID",
          value: fixedDetails.utilityDetails.scheduleId || "N/A",
        },
      ];

      // Calculate table height
      const rowHeight = 12;
      const headerHeight = 10;
      const tableHeight = headerHeight + utilityData.length * rowHeight;

      checkPageBreak(tableHeight + 15);

      // Draw table border
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(75, 91, 93);
      pdf.rect(margin, currentY, contentWidth, tableHeight);

      // Table header with gradient background
      pdf.setFillColor(244, 239, 230);
      pdf.rect(margin, currentY, contentWidth, headerHeight, "F");

      // Header text
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.text("Field", margin + 5, currentY + 7);
      pdf.text("Value", margin + contentWidth / 2 + 5, currentY + 7);

      currentY += headerHeight;

      // Table rows with alternating background
      utilityData.forEach((item, index) => {
        // Alternating row background
        pdf.setFillColor(index % 2 === 0 ? 250 : 245, 250, 245);
        pdf.rect(margin, currentY, contentWidth, rowHeight, "F");

        // Field name
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }

        // Handle long field names with text wrapping
        const fieldLines = splitTextIntoLines(
          item.field,
          contentWidth / 2 - 10,
          pdf,
          9
        );
        fieldLines.forEach((line, lineIndex) => {
          pdf.text(line, margin + 5, currentY + 5 + lineIndex * 3);
        });

        // Field value
        pdf.setFont("helvetica", "normal");

        // Special formatting for Data Source with color coding
        if (item.field === "Data Source") {
          pdf.setFont("helvetica", "bold");
          if (item.value === "Real-time Utility Lookup") {
            pdf.setTextColor(46, 125, 50);
          } else {
            pdf.setTextColor(21, 101, 192);
          }
        } else if (item.field === "Electricity Rate") {
          pdf.setFont("helvetica", "bold");
          if (isWhiteLabel && whiteLabelConfig) {
            const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
            pdf.setTextColor(r, g, b);
          } else {
            pdf.setTextColor(75, 91, 93);
          }
        } else {
          if (isWhiteLabel && whiteLabelConfig) {
            const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
            pdf.setTextColor(r, g, b);
          } else {
            pdf.setTextColor(75, 91, 93);
          }
        }

        const valueLines = splitTextIntoLines(
          item.value,
          contentWidth / 2 - 10,
          pdf,
          9
        );
        valueLines.forEach((line, lineIndex) => {
          pdf.text(
            line,
            margin + contentWidth / 2 + 5,
            currentY + 5 + lineIndex * 3
          );
        });

        currentY += Math.max(
          rowHeight,
          fieldLines.length * 3,
          valueLines.length * 3
        );
      });

      currentY += 10;
      checkPageBreak(25);
    }

    // Energy Costs Breakdown Section
    const shouldRenderEnergyCostsSection = hasRealUtilityData(fixedDetails);

    if (shouldRenderEnergyCostsSection && fixedDetails.energyCosts) {
      checkPageBreak(30);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.text("Energy Costs Breakdown", margin, currentY);
      currentY += 10;

      // Energy costs table
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(75, 91, 93);
      pdf.rect(margin, currentY, contentWidth, 10 + 9 * 10);

      // Table header
      pdf.setFillColor(245, 240, 231);
      pdf.rect(margin, currentY, contentWidth, 10, "F");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      const energyColumns = [
        "Category",
        "Year 1",
        "Year 2",
        "Year 3",
        "Year 4",
        "Year 5",
      ];
      const energyColWidth = contentWidth / 6;
      energyColumns.forEach((col, idx) => {
        pdf.text(col, margin + idx * energyColWidth + 3, currentY + 7);
      });
      currentY += 10;

      // Energy cost rows
      const energyRows = [
        {
          category: "Energy",
          data: toNumArr(fixedDetails.energyCosts.energy),
        },
        {
          category: "Energy Time",
          data: toNumArr(fixedDetails.energyCosts.energyTime),
        },
        {
          category: "Incremental Energy",
          data: toNumArr(fixedDetails.energyCosts.incrementalEnergy),
        },
        {
          category: "Demand",
          data: toNumArr(fixedDetails.energyCosts.demand),
        },
        {
          category: "Demand Time",
          data: toNumArr(fixedDetails.energyCosts.demandTime),
        },
        {
          category: "Incremental Demand",
          data: toNumArr(fixedDetails.energyCosts.incrementalDemand),
        },
        {
          category: "Service Charge",
          data: toNumArr(fixedDetails.energyCosts.serviceCharge),
        },
        {
          category: "Other Charges",
          data: toNumArr(fixedDetails.energyCosts.otherCharges),
        },
        {
          category: "Total",
          data: toNumArr(fixedDetails.energyCosts.total),
        },
      ];

      energyRows.forEach((row, index) => {
        checkPageBreak(10);
        pdf.setFillColor(
          index % 2 === 0 ? 245 : 237,
          index % 2 === 0 ? 240 : 231,
          index % 2 === 0 ? 231 : 219
        );
        pdf.rect(margin, currentY, contentWidth, 10, "F");
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.text(row.category, margin + 3, currentY + 7);
        pdf.setFont("helvetica", "normal");
        row.data.forEach((value, yearIdx) => {
          pdf.text(
            `$${value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
            margin + (yearIdx + 1) * energyColWidth + 5,
            currentY + 7
          );
        });
        currentY += 10;
      });
      currentY += 15;
    }

    // Tax Benefits Section
    if (fixedDetails.applyTaxBenefits && fixedDetails.taxBenefitsTable) {
      checkPageBreak(150);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.text("Tax Benefits Details", margin, currentY);
      currentY += 10;

      // Draw tax benefits table
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(75, 91, 93);
      pdf.rect(
        margin,
        currentY,
        contentWidth,
        10 + fixedDetails.taxBenefitsTable.length * 10
      );

      // Table header
      pdf.setFillColor(245, 240, 231);
      pdf.rect(margin, currentY, contentWidth, 10, "F");
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      pdf.text("Category", margin + 5, currentY + 7);
      pdf.text("Value", margin + contentWidth / 2 + 5, currentY + 7);
      currentY += 10;

      // Table rows
      fixedDetails.taxBenefitsTable.forEach((item, index) => {
        checkPageBreak(10);
        pdf.setFillColor(
          index % 2 === 0 ? 245 : 237,
          index % 2 === 0 ? 240 : 231,
          index % 2 === 0 ? 231 : 219
        );
        pdf.rect(margin, currentY, contentWidth, 10, "F");
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");

        // Handle long category names
        const categoryLines = splitTextIntoLines(
          item.category,
          contentWidth / 2 - 10,
          pdf,
          9
        );
        categoryLines.forEach((line, lineIndex) => {
          pdf.text(line, margin + 5, currentY + 7 + lineIndex * 3);
        });

        pdf.setFont("helvetica", "normal");
        pdf.text(
          String(item.value),
          margin + contentWidth / 2 + 5,
          currentY + 7
        );
        currentY += Math.max(10, categoryLines.length * 3);
      });
      currentY += 10;

      // Tax disclaimer
      checkPageBreak(20);
      pdf.setFillColor(255, 243, 205);
      pdf.rect(margin, currentY, contentWidth, 15, "F");
      pdf.setFontSize(7);
      pdf.setTextColor(133, 100, 4);
      const disclaimerText =
        "Tax disclaimer: Estimates are illustrative only and do not constitute tax advice. Section 179 and 168(k) eligibility and limits vary by entity and state. Consult a qualified tax professional to confirm treatment for your situation.";
      const disclaimerLines = splitTextIntoLines(
        disclaimerText,
        contentWidth - 10,
        pdf,
        7
      );
      disclaimerLines.forEach((line, lineIndex) => {
        pdf.text(line, margin + 5, currentY + 5 + lineIndex * 3);
      });
      currentY += 30;
    }

    // Charts Section
    const chartEntries = Object.entries(charts).filter(([, chart]) => chart);
    for (let i = 0; i < chartEntries.length; i++) {
      checkPageBreak(chartHeight + 25);

      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }

      const chartName = chartEntries[i][0];
      const chartTitle =
        chartName === "costOverTime"
          ? "Cost of Gasoline vs Electric Over Time"
          : chartName === "cumulativeSavings"
          ? "Break-even Timeline (Cumulative Net Savings)"
          : chartName === "environmental"
          ? "Environmental Impact (Annual)"
          : "Annual Cost Comparison";

      pdf.text(chartTitle, margin, currentY);
      currentY += 8;

      const chart = chartEntries[i][1] as Chart;
      const chartCanvas = chart.canvas as HTMLCanvasElement;
      const chartImageData = chartCanvas.toDataURL(
        "image/jpeg",
        PDF_IMAGE_QUALITY
      );

      pdf.addImage(
        chartImageData,
        "JPEG",
        margin,
        currentY,
        contentWidth,
        chartHeight,
        undefined,
        "FAST"
      );
      currentY += chartHeight + 15;

      // Add chart notes
      if (chartName === "costOverTime") {
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(100, 100, 100);
        pdf.text(
          "Annual Opex with U.S. average yearly price growth (gas ~3.9%/yr; electricity ~2.5%/yr).",
          margin,
          currentY
        );
        currentY += 5;
      } else if (chartName === "environmental") {
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(100, 100, 100);
        pdf.text(
          "Showing annual environmental impact including gasoline avoided, tailpipe CO2 reduction, and net CO2 impact considering grid emissions.",
          margin,
          currentY
        );
        currentY += 5;
      }
    }

    // Add Terms & Conditions and Privacy Policy for white label
    if (isWhiteLabel && whiteLabelConfig) {
      checkPageBreak(30);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
      pdf.setTextColor(r, g, b);
      pdf.text("Additional Information", margin, currentY);
      currentY += 10;

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");

      // Helper function to add clickable links with wrapping
      const addClickableLink = (
        label: string,
        url: string,
        maxWidth: number
      ) => {
        checkPageBreak(20);

        // Add label
        pdf.setTextColor(r, g, b);
        pdf.text(`${label}:`, margin, currentY);

        // Calculate available width for URL (accounting for label width)
        const labelWidth = pdf.getTextWidth(`${label}: `);
        const urlMaxWidth = maxWidth - labelWidth;

        // Split URL into multiple lines if needed
        const urlLines = splitTextIntoLines(url, urlMaxWidth, pdf, 12);

        // Add URL as clickable text
        pdf.setTextColor(0, 0, 255);
        urlLines.forEach((line, index) => {
          if (index === 0) {
            pdf.textWithLink(line, margin + labelWidth, currentY, { url });
          } else {
            pdf.text(line, margin + labelWidth, currentY + index * 4);
          }
        });

        // Update currentY based on number of URL lines
        currentY += Math.max(12, urlLines.length * 4 + 5);
      };

      if (whiteLabelConfig.termsOfServiceURL) {
        addClickableLink(
          "Terms & Conditions",
          whiteLabelConfig.termsOfServiceURL,
          contentWidth
        );
      }

      if (whiteLabelConfig.privacyPolicyURL) {
        addClickableLink(
          "Privacy Policy",
          whiteLabelConfig.privacyPolicyURL,
          contentWidth
        );
      }

      // Reset text color
      pdf.setTextColor(r, g, b);
    }

    // Destroy charts after rendering
    Object.values(charts).forEach((chart) => chart?.destroy());

    // Save or return the PDF
    const filenameBase = (
      fixedDetails.reportName || "Fleet_EV_Conversion"
    ).replace(/[^a-z0-9]+/gi, "_");
    const filename = `${filenameBase}_${isWhiteLabel ? "WL" : "Report"}_${
      new Date().toISOString().split("T")[0]
    }.pdf`;

    if (returnBlob) {
      const pdfAny = pdf as unknown as {
        output: (
          type: string,
          options?: { compress?: boolean }
        ) => ArrayBuffer | string;
      };
      const arrayBuffer = pdfAny.output("arraybuffer", {
        compress: true,
      }) as ArrayBuffer;
      return new Blob([arrayBuffer], { type: "application/pdf" });
    }
    pdf.save(filename);
  } catch (err) {
    console.error("Fleet PDF generation failed", err);
    throw err;
  }
};
